import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Link from 'gatsby-link';
import { css } from 'react-emotion';
import classnames from 'classnames';
import Color from 'color';
import defer from 'lodash/fp/defer';
import noScroll from 'no-scroll';

import events from '../utils/events';
import { navItems } from '../utils/config';
import { colors, animation, baselineGrid, zIndex } from '../utils/presets';
import { toRem } from '../utils/functions';
import { options } from '../utils/typography';
import { easeInOut } from '../utils/ease';

import InnerWrapper from '../components/inner-wrapper';
import MenuToggle from '../components/menu-toggle';

import Logo from '../assets/svg-sprite/pixelmoxie-logo.svg';

const { curve, speed } = animation;

const MENU_OPEN_CLASSES = ['menu-open', 'prevent-scroll'];

const logoColor = Color(colors.ui.whisper)
  .alpha(0.7)
  .hsl()
  .toString();

// Staggered delays
const transitionDelay = parseFloat(speed.fast);
const staggeredDelays = Array(navItems.length)
  .fill()
  .map((_, idx) => easeInOut(2)(1 / (navItems.length - 1) * idx) * transitionDelay);

// Staggered offsets
const verticalOffset = 24;
const staggeredOffsets = Array(navItems.length)
  .fill()
  .map((_, idx) => parseInt(easeInOut(1)(1 / (navItems.length - 1) * idx) * verticalOffset, 10));

const overlayLogo = css`
  display: inline-block;
  vertical-align: middle;
  width: 168px;
  height: 24px;
  margin: ${toRem(baselineGrid * 0.5)} 0;
  fill: ${logoColor};
  opacity: 0;
  transition: opacity ${speed.medium} ${curve.default};
  pointer-events: none;

  .menu-open & {
    opacity: 1;
  }
`;

const overlayTopBar = css`
  padding: ${toRem(baselineGrid * 3)} 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const overlayMenuItem = css`
  display: block;
  padding: ${toRem(baselineGrid)};
  font-family: ${options.headerFontFamily.join(',')};
  font-size: ${options.overlayFontSize};
  font-size: calc(${options.overlayFontSize} + 1.5625vw);
  font-weight: 700;
  text-decoration: none;
  color: ${colors.ui.bright};
`;

const overlayMenuItemList = css`
  height: calc(100vh - ${toRem(baselineGrid * 10)});
  padding: calc(${toRem(baselineGrid * 3)} + 3.59vh) ${toRem(baselineGrid * 3)};
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
`;

const overlayMenu = css`
  visibility: hidden;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: ${colors.ui.flush};
  z-index: ${zIndex[9]};

  .menu-open & {
    visibility: visible;
  }
`;

const listItemsStyles = {
  textAlign: 'center',
  opacity: 0,
  transition: 'none',
};

const activeListItemsStyles = {
  opacity: 1,
  transform: 'translate(0px, 0px)',
  transition: `transform ${speed.default} ${curve.default}, opacity ${speed.medium} ${
    curve.default
  }`,
};

const MenuItem = ({ idx, linkTo, children, onClick }) => (
  <div
    css={{
      ...listItemsStyles,
      transform: `translate(-25vw, ${staggeredOffsets[idx]}px)`,
      '.menu-open &': {
        ...activeListItemsStyles,
        transitionDelay: `${staggeredDelays[idx]}ms`,
      },
    }}
  >
    <Link to={linkTo} onClick={onClick} css={overlayMenuItem}>
      {children}
    </Link>
  </div>
);

MenuItem.propTypes = {
  idx: PropTypes.number.isRequired,
  linkTo: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func.isRequired,
};

class OverlayMenu extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: false,
    };

    this.toggleMenu = this.toggleMenu.bind(this);
  }

  componentDidMount() {
    events.on('navToggleClicked', this.toggleMenu);
  }

  componentWillUnmount() {
    events.off('navToggleClicked', this.toggleMenu);
  }

  toggleMenu(state) {
    this.setState(() => {
      if (state && Object.prototype.hasOwnProperty.call(state, 'isOpen')) {
        return {
          isOpen: state.isOpen,
        };
      }

      return {
        isOpen: !this.state.isOpen,
      };
    });

    defer(() => {
      if (this.state.isOpen) noScroll.on();
      else noScroll.off();
    });
  }

  componentDidUpdate(nextProps, nextState) {
    if (Object.prototype.hasOwnProperty.call(nextState, 'isOpen')) {
      setTimeout(() => {
        if (this.state.isOpen) {
          document.body.classList.add(...MENU_OPEN_CLASSES);
        } else {
          document.body.classList.remove(...MENU_OPEN_CLASSES);
        }
      }, 0);
    }
  }

  render() {
    return (
      <div className={classnames({ 'is-open': this.state.isOpen })} css={overlayMenu}>
        <InnerWrapper>
          <div css={overlayTopBar}>
            <svg viewBox={Logo.viewBox} css={overlayLogo}>
              <use xlinkHref={`#${Logo.id}`} />
            </svg>
            <MenuToggle
              label="Navigation Menu"
              color={colors.ui.bright}
              onClick={() => {
                this.toggleMenu({ isOpen: false });
              }}
            />
          </div>
          <div css={overlayMenuItemList}>
            {navItems.sort((a, b) => a.order - b.order).map((item, index) => (
              <MenuItem
                key={index}
                idx={index}
                linkTo={item.path}
                onClick={() => {
                  this.toggleMenu({ isOpen: false });
                }}
              >
                {item.label}
              </MenuItem>
            ))}
            {this.props.children}
          </div>
        </InnerWrapper>
      </div>
    );
  }
}

OverlayMenu.propTypes = {
  children: PropTypes.node,
};

export default OverlayMenu;



// WEBPACK FOOTER //
// ./src/components/overlay-menu.js